// M13 Notification & Alert Center — /notifications (Admin + Recruiter; future
// Back Office/Accounts reuse the same page once those roles are activated).
// Informational layer: "What just happened?" — complements the M12 Work Queue.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle, Archive, ArchiveRestore, ArrowRight, Bell, BellRing, CheckCheck, Check,
  ChevronLeft, ChevronRight, Clock, ExternalLink, History, Inbox, Megaphone, RefreshCw, Search,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { formatDateTime } from "@/lib/constants";
import { resolveNotificationLink } from "@/lib/notificationLinks";
import { useAuth } from "@/context/AuthContext";

const PRIORITY_STYLES = {
  Critical: "bg-red-50 text-red-700 border-red-200",
  High: "bg-amber-50 text-amber-700 border-amber-200",
  Normal: "bg-sky-50 text-sky-700 border-sky-200",
  Low: "bg-slate-50 text-slate-600 border-slate-200",
};

const STATUS_STYLES = {
  Unread: "bg-primary/10 text-primary border-primary/20",
  Read: "bg-slate-50 text-slate-600 border-slate-200",
  Archived: "bg-slate-100 text-slate-500 border-slate-200",
};

const PriorityBadge = ({ value }) => (
  <Badge variant="outline" className={`rounded-full ${PRIORITY_STYLES[value] || PRIORITY_STYLES.Normal}`}
         data-testid="notification-priority-badge">
    {value}
  </Badge>
);

const StatusPill = ({ value }) => (
  <Badge variant="outline" className={`rounded-full ${STATUS_STYLES[value] || STATUS_STYLES.Read}`}
         data-testid="notification-status-badge">
    {value}
  </Badge>
);

const BUCKETS = [
  { key: "active", label: "Active" },
  { key: "unread", label: "Unread" },
  { key: "read", label: "Read" },
  { key: "archived", label: "Archived" },
  { key: "all", label: "All" },
];

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  const [tab, setTab] = useState("inbox"); // inbox | announcements | reports (admin extras)
  const [bucket, setBucket] = useState("active");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState(null);

  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [fType, setFType] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [fModule, setFModule] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [scopeAll, setScopeAll] = useState(false);
  const [fRecipient, setFRecipient] = useState("all");

  const [historyFor, setHistoryFor] = useState(null);
  const [historyEvents, setHistoryEvents] = useState(null);

  // announcements (admin)
  const [announcements, setAnnouncements] = useState(null);
  const [annOpen, setAnnOpen] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", message: "", audience: "all_users", priority: "Normal", user_ids: [] });
  const [annSaving, setAnnSaving] = useState(false);

  // reports (admin)
  const [reports, setReports] = useState(null);
  const [reportView, setReportView] = useState("all");

  const loadSummary = useCallback(() => {
    api.get("/notifications/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { page, limit: 25, bucket: bucket === "all" ? undefined : bucket, q: q || undefined };
    if (fType !== "all") params.notification_type = fType;
    if (fPriority !== "all") params.priority = fPriority;
    if (fModule !== "all") params.module = fModule;
    if (fSource !== "all") params.source = fSource;
    if (isAdmin && scopeAll) {
      params.scope = "all";
      if (fRecipient !== "all") params.recipient_id = fRecipient;
    }
    api.get("/notifications", { params })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); setPages(r.data.pages); })
      .catch((e) => setError(getErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [page, bucket, q, fType, fPriority, fModule, fSource, scopeAll, fRecipient, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    api.get("/notifications/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(() => { setQ(qInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [qInput]);
  useEffect(() => {
    if (tab === "reports" && isAdmin) {
      api.get("/notifications/reports", { params: { view: reportView } })
        .then((r) => setReports(r.data)).catch((e) => toast.error(getErrorMessage(e)));
    }
    if (tab === "announcements" && isAdmin) {
      api.get("/notifications/announcements")
        .then((r) => setAnnouncements(r.data)).catch((e) => toast.error(getErrorMessage(e)));
    }
  }, [tab, reportView, isAdmin]);

  const refreshAll = () => { load(); loadSummary(); };

  const act = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      refreshAll();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const openRecord = async (n) => {
    if (n.recipient_user_id === user.id && n.status === "Unread") {
      try { await api.put(`/notifications/${n.id}/read`); } catch { /* non-blocking */ }
    }
    const link = resolveNotificationLink(n, user.role);
    if (link) navigate(link);
    else refreshAll();
  };

  const showHistory = (n) => {
    setHistoryFor(n);
    setHistoryEvents(null);
    api.get(`/notifications/${n.id}/history`)
      .then((r) => setHistoryEvents(r.data))
      .catch((e) => { toast.error(getErrorMessage(e)); setHistoryFor(null); });
  };

  const sendAnnouncement = async () => {
    setAnnSaving(true);
    try {
      const payload = { ...annForm, user_ids: annForm.audience === "specific_users" ? annForm.user_ids : undefined };
      const res = await api.post("/notifications/announcements", payload);
      toast.success(`Announcement sent to ${res.data.recipient_count} user(s)`);
      setAnnOpen(false);
      setAnnForm({ title: "", message: "", audience: "all_users", priority: "Normal", user_ids: [] });
      api.get("/notifications/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
      refreshAll();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setAnnSaving(false);
    }
  };

  const kpis = useMemo(() => ({
    unread: summary?.unread ?? 0,
    critical: summary?.critical_unread ?? 0,
    recent: summary?.recent?.length ?? 0,
    alerts: summary?.alerts?.length ?? 0,
  }), [summary]);

  const own = (n) => n.recipient_user_id === user.id;

  return (
    <div className="space-y-6" data-testid={`notifications-page-${user.role === "recruiter" ? "recruiter" : "admin"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BellRing className="h-6 w-6 text-primary" /> Notification Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            What just happened across your operations — actionable work stays in the Work Queue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={refreshAll} data-testid="notifications-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" data-testid="mark-all-read-button"
                  disabled={kpis.unread === 0}
                  onClick={() => act(() => api.put("/notifications/mark-all-read"), "All notifications marked as read")}>
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </Button>
          {isAdmin && (
            <Button size="sm" className="gap-1.5" onClick={() => setAnnOpen(true)} data-testid="new-announcement-button">
              <Megaphone className="h-3.5 w-3.5" /> New announcement
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Unread" value={kpis.unread} icon={Bell} tone="primary" testId="kpi-unread" />
        <KpiCard label="Critical unread" value={kpis.critical} icon={AlertTriangle} tone="danger" testId="kpi-critical" />
        <KpiCard label="Recent activity" value={kpis.recent} icon={Clock} tone="violet" testId="kpi-recent" />
        <KpiCard label="Alerts & announcements" value={kpis.alerts} icon={Megaphone} tone="warning" testId="kpi-alerts" />
      </div>

      {isAdmin && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {tab === "inbox" && (
        <>
          <Card>
            <CardContent className="pt-5 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={qInput} onChange={(e) => setQInput(e.target.value)}
                         placeholder="Search title, message, candidate, company, ID..."
                         className="pl-9" data-testid="notifications-search" />
                </div>
                <Select value={fType} onValueChange={(v) => { setFType(v); setPage(1); }}>
                  <SelectTrigger className="w-[190px]" data-testid="filter-type">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {(options?.types || []).map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={fPriority} onValueChange={(v) => { setFPriority(v); setPage(1); }}>
                  <SelectTrigger className="w-[130px]" data-testid="filter-priority">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All priorities</SelectItem>
                    {(options?.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fModule} onValueChange={(v) => { setFModule(v); setPage(1); }}>
                  <SelectTrigger className="w-[120px]" data-testid="filter-module">
                    <SelectValue placeholder="Module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All modules</SelectItem>
                    {(options?.modules || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={fSource} onValueChange={(v) => { setFSource(v); setPage(1); }}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-source">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sources</SelectItem>
                    {(options?.sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <>
                    <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                      <Checkbox checked={scopeAll} onCheckedChange={(v) => { setScopeAll(!!v); setPage(1); }}
                                data-testid="scope-all-toggle" />
                      All users
                    </label>
                    {scopeAll && (
                      <Select value={fRecipient} onValueChange={(v) => { setFRecipient(v); setPage(1); }}>
                        <SelectTrigger className="w-[180px]" data-testid="filter-recipient">
                          <SelectValue placeholder="Recipient" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All recipients</SelectItem>
                          {(options?.users || []).map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </>
                )}
              </div>

              <Tabs value={bucket} onValueChange={(v) => { setBucket(v); setPage(1); }}>
                <TabsList>
                  {BUCKETS.map((b) => (
                    <TabsTrigger key={b.key} value={b.key} data-testid={`bucket-${b.key}`}>{b.label}</TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-3" data-testid="notifications-loading">
                  {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
                </div>
              ) : error ? (
                <div className="p-10 text-center" data-testid="notifications-error">
                  <p className="text-sm text-red-600 mb-3">{error}</p>
                  <Button variant="outline" onClick={load}>Retry</Button>
                </div>
              ) : items.length === 0 ? (
                <div className="py-14" data-testid="notifications-empty">
                  <EmptyState icon={Inbox} title="No notifications here"
                              description="When business events happen — assignments, interviews, joinings, billing — they will show up here." />
                </div>
              ) : (
                <ul className="divide-y divide-border" data-testid="notifications-list">
                  {items.map((n) => (
                    <li key={n.id} className={`px-4 sm:px-6 py-4 ${n.status === "Unread" ? "bg-primary/[0.03]" : ""}`}
                        data-testid={`notification-row-${n.notify_code}`}>
                      <div className="flex flex-wrap items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-[11px] text-muted-foreground">{n.notify_code}</span>
                            <Badge variant="outline" className="rounded-full text-[11px]">{n.notification_type}</Badge>
                            <PriorityBadge value={n.priority} />
                            <StatusPill value={n.status} />
                            {n.merge_count > 0 && (
                              <Badge variant="secondary" className="rounded-full text-[11px]" data-testid="merge-count-badge">
                                updated ×{n.merge_count}
                              </Badge>
                            )}
                            {n.notification_source === "Admin Broadcast" && (
                              <Badge variant="secondary" className="rounded-full text-[11px] gap-1">
                                <Megaphone className="h-3 w-3" /> Broadcast
                              </Badge>
                            )}
                          </div>
                          <p className={`mt-1.5 text-sm ${n.status === "Unread" ? "font-semibold" : "font-medium"}`}>{n.title}</p>
                          <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                          <p className="text-xs text-muted-foreground/80 mt-1.5">
                            {n.source_module} · {formatDateTime(n.last_merged_at || n.created_at)}
                            {isAdmin && scopeAll && n.recipient_user_name ? ` · to ${n.recipient_user_name}` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {resolveNotificationLink(n, user.role) && (
                            <Button variant="outline" size="sm" className="gap-1.5"
                                    onClick={() => openRecord(n)} data-testid={`open-record-${n.notify_code}`}>
                              <ExternalLink className="h-3.5 w-3.5" /> Open
                            </Button>
                          )}
                          {own(n) && n.status === "Unread" && (
                            <Button variant="ghost" size="sm" className="gap-1.5"
                                    onClick={() => act(() => api.put(`/notifications/${n.id}/read`))}
                                    data-testid={`mark-read-${n.notify_code}`}>
                              <Check className="h-3.5 w-3.5" /> Read
                            </Button>
                          )}
                          {own(n) && n.status === "Read" && (
                            <Button variant="ghost" size="sm" className="gap-1.5"
                                    onClick={() => act(() => api.put(`/notifications/${n.id}/archive`), "Notification archived")}
                                    data-testid={`archive-${n.notify_code}`}>
                              <Archive className="h-3.5 w-3.5" /> Archive
                            </Button>
                          )}
                          {own(n) && n.status === "Archived" && (
                            <Button variant="ghost" size="sm" className="gap-1.5"
                                    onClick={() => act(() => api.put(`/notifications/${n.id}/restore`), "Notification restored")}
                                    data-testid={`restore-${n.notify_code}`}>
                              <ArchiveRestore className="h-3.5 w-3.5" /> Restore
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => showHistory(n)}
                                  data-testid={`history-${n.notify_code}`} aria-label="Audit history">
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground" data-testid="notifications-total">
              {total} notification{total === 1 ? "" : "s"}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                      data-testid="page-prev"><ChevronLeft className="h-4 w-4" /> Prev</Button>
              <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                      data-testid="page-next">Next <ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </>
      )}

      {tab === "announcements" && isAdmin && (
        <Card data-testid="announcements-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" /> Announcement history
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!announcements ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}</div>
            ) : announcements.length === 0 ? (
              <EmptyState icon={Megaphone} title="No announcements yet"
                          description="Broadcast office updates, holidays, maintenance windows or policy changes."
                          actionLabel="New announcement" onAction={() => setAnnOpen(true)} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Sent by</TableHead>
                    <TableHead>Sent at</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {announcements.map((a) => (
                    <TableRow key={a.id} data-testid={`announcement-row-${a.id}`}>
                      <TableCell>
                        <p className="font-medium">{a.title}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{a.message}</p>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full">{a.audience_label}</Badge></TableCell>
                      <TableCell><PriorityBadge value={a.priority} /></TableCell>
                      <TableCell>{a.recipient_count}</TableCell>
                      <TableCell>{a.created_by_name}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDateTime(a.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "reports" && isAdmin && (
        <div className="space-y-4" data-testid="notification-reports-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Notification analytics — read rate, volume and attention gaps.</p>
            <Select value={reportView} onValueChange={setReportView}>
              <SelectTrigger className="w-[160px]" data-testid="report-view-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All notifications</SelectItem>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="archived">Archived only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!reports ? (
            <div className="grid md:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard label="Total" value={reports.totals.total} icon={Bell} tone="primary" testId="report-total" />
                <KpiCard label="Read rate" value={`${reports.totals.read_rate_pct}%`} icon={CheckCheck} tone="success" testId="report-read-rate" />
                <KpiCard label="Critical" value={reports.totals.critical} icon={AlertTriangle} tone="danger" testId="report-critical" />
                <KpiCard label="Unread > 48h" value={reports.totals.overdue_unread_48h} icon={Clock} tone="warning" testId="report-overdue" />
              </div>
              <div className="grid lg:grid-cols-2 gap-4">
                <Card data-testid="report-by-user">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Volume by user</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Unread</TableHead><TableHead className="text-right">Critical</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.by_user.map((r) => (
                          <TableRow key={r.user_id || r.name}>
                            <TableCell className="font-medium">{r.name}</TableCell>
                            <TableCell className="capitalize text-muted-foreground">{(r.role || "").replace("_", " ")}</TableCell>
                            <TableCell className="text-right">{r.total}</TableCell>
                            <TableCell className="text-right">{r.unread}</TableCell>
                            <TableCell className="text-right">{r.critical}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card data-testid="report-by-type">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Volume by type</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow><TableHead>Type</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Unread</TableHead><TableHead className="text-right">Critical</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.by_type.map((r) => (
                          <TableRow key={r.key}>
                            <TableCell className="font-medium">{r.key}</TableCell>
                            <TableCell className="text-right">{r.total}</TableCell>
                            <TableCell className="text-right">{r.unread}</TableCell>
                            <TableCell className="text-right">{r.critical}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card data-testid="report-by-module">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Volume by module</CardTitle></CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow><TableHead>Module</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Unread</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {reports.by_module.map((r) => (
                          <TableRow key={r.key}>
                            <TableCell className="font-medium">{r.key}</TableCell>
                            <TableCell className="text-right">{r.total}</TableCell>
                            <TableCell className="text-right">{r.unread}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <Card data-testid="report-status-split">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Status & delivery</CardTitle></CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Unread</span><span className="font-semibold">{reports.totals.unread}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Read</span><span className="font-semibold">{reports.totals.read}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Archived</span><span className="font-semibold">{reports.totals.archived}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Critical unread</span><span className="font-semibold text-red-600">{reports.totals.critical_unread}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Announcements sent</span><span className="font-semibold">{reports.totals.announcements}</span></div>
                    <div className="pt-2 border-t border-border text-xs text-muted-foreground" data-testid="scheduler-state">
                      Reminder engine: {reports.scheduler.run_count} pass(es)
                      {reports.scheduler.last_run_at ? ` · last ${formatDateTime(reports.scheduler.last_run_at)}` : ""}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* audit history dialog */}
      <Dialog open={!!historyFor} onOpenChange={(v) => !v && setHistoryFor(null)}>
        <DialogContent className="max-w-lg" data-testid="notification-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-primary" /> Audit history — {historyFor?.notify_code}
            </DialogTitle>
            <DialogDescription>Immutable event trail. Entries are never edited or deleted.</DialogDescription>
          </DialogHeader>
          {!historyEvents ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
          ) : (
            <ol className="relative border-l border-border ml-2 space-y-4 max-h-[50vh] overflow-y-auto pr-2">
              {historyEvents.map((e) => (
                <li key={e.id} className="ml-4">
                  <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                  <p className="text-sm font-medium">{e.event_type.replaceAll("_", " ")}</p>
                  {e.remarks && <p className="text-xs text-muted-foreground">{e.remarks}</p>}
                  <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                    {e.actor_name} · {formatDateTime(e.at)}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>

      {/* announcement composer (admin) */}
      <Dialog open={annOpen} onOpenChange={setAnnOpen}>
        <DialogContent className="max-w-lg" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" /> New announcement
            </DialogTitle>
            <DialogDescription>
              Broadcast to the selected audience — every recipient gets their own notification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ann-title">Title</Label>
              <Input id="ann-title" value={annForm.title} maxLength={160}
                     onChange={(e) => setAnnForm((f) => ({ ...f, title: e.target.value }))}
                     placeholder="e.g. Office closed on Friday" data-testid="announcement-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ann-msg">Message</Label>
              <Textarea id="ann-msg" value={annForm.message} rows={4} maxLength={2000}
                        onChange={(e) => setAnnForm((f) => ({ ...f, message: e.target.value }))}
                        placeholder="Details everyone should know..." data-testid="announcement-message-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Audience</Label>
                <Select value={annForm.audience}
                        onValueChange={(v) => setAnnForm((f) => ({ ...f, audience: v }))}>
                  <SelectTrigger data-testid="announcement-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options?.audiences || []).map((a) => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={annForm.priority}
                        onValueChange={(v) => setAnnForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="announcement-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(options?.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {annForm.audience === "specific_users" && (
              <div className="space-y-1.5">
                <Label>Recipients</Label>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border p-2 space-y-1"
                     data-testid="announcement-user-list">
                  {(options?.users || []).map((u) => (
                    <label key={u.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded hover:bg-muted cursor-pointer">
                      <Checkbox
                        checked={annForm.user_ids.includes(u.id)}
                        onCheckedChange={(v) => setAnnForm((f) => ({
                          ...f,
                          user_ids: v ? [...f.user_ids, u.id] : f.user_ids.filter((x) => x !== u.id),
                        }))}
                        data-testid={`announcement-user-${u.id}`}
                      />
                      {u.name} <span className="text-xs text-muted-foreground capitalize">({(u.role || "").replace("_", " ")})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnnOpen(false)} data-testid="announcement-cancel">Cancel</Button>
            <Button onClick={sendAnnouncement} data-testid="announcement-send"
                    disabled={annSaving || annForm.title.trim().length < 3 || annForm.message.trim().length < 3 ||
                              (annForm.audience === "specific_users" && annForm.user_ids.length === 0)}>
              {annSaving ? "Sending..." : "Send announcement"} <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
